import { getOperationAST, OperationTypeNode, parse } from "graphql";
import type { QueryPayload } from "gqty";

export const GRAPHQL_REQUEST_TIMEOUT_MS = 60_000;

export function withQueryRequestDeadline<T>(
  payload: Pick<QueryPayload, "query" | "operationName">,
  request: (signal?: AbortSignal | null) => Promise<T>,
  callerSignal?: AbortSignal | null,
): Promise<T> {
  // A timed-out write could still finish on the server. Preserve mutation
  // transport behavior instead of adding a new reason to retry that write.
  if (
    getOperationAST(parse(payload.query), payload.operationName)?.operation !==
    OperationTypeNode.QUERY
  ) {
    return request(callerSignal);
  }
  return withRequestDeadline(request, callerSignal);
}

export async function withRequestDeadline<T>(
  request: (signal: AbortSignal) => Promise<T>,
  callerSignal?: AbortSignal | null,
): Promise<T> {
  callerSignal?.throwIfAborted();
  const controller = new AbortController();
  let interrupt = (_reason: unknown) => {};
  const interrupted = new Promise<never>((_, reject) => {
    interrupt = (reason: unknown) => {
      controller.abort(reason);
      reject(controller.signal.reason);
    };
  });
  const cancel = () => interrupt(callerSignal?.reason);
  callerSignal?.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(() => {
    interrupt(
      new DOMException("The query timed out. Please try again.", "TimeoutError"),
    );
  }, GRAPHQL_REQUEST_TIMEOUT_MS);

  try {
    // Authentication can be pending before fetch sees its signal. Settle the
    // caller at the deadline even when that work cannot itself be aborted.
    return await Promise.race([
      Promise.resolve().then(() => {
        controller.signal.throwIfAborted();
        return request(controller.signal);
      }),
      interrupted,
    ]);
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener("abort", cancel);
  }
}
